import { MiniTooltip, Theme } from '@spoiler-alert/ui-library';
import { ArcElement, Chart as ChartJS } from 'chart.js';
import PropTypes from 'prop-types';
import React from 'react';
import { Doughnut } from 'react-chartjs-2';
import { createUseStyles } from 'react-jss';

ChartJS.register(ArcElement);
const useStyles = createUseStyles({
  chartContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    maxWidth: '350px',
    height: '58px',
  },
  chartText: {
    fontSize: 12,
    fontWeight: 400,
    lineHeight: '16px',
  },
  tooltipText: {
    fontWeight: 'normal',
  },
  doughnut: {
    width: 58,
  },
});

const RateChart = ({
  progress,
  cutout = '50%',
  subjectColor = Theme.green,
  backgroundColor = Theme.green10,
  animation = false,
  chartText = '',
  toolTipText = '',
  iqEnabled = false,
}) => {
  const classes = useStyles();
  let width;
  let height;
  let gradient;

  const chartPercentage = () => {
    if (progress > 100) return [0, 100];
    return [100 - (progress || 1), progress || 1];
  };

  const getGradient = (ctx, chartArea) => {
    const chartWidth = chartArea.right - chartArea.left;
    const chartHeight = chartArea.bottom - chartArea.top;
    if (!gradient || width !== chartWidth || height !== chartHeight) {
      // Create the gradient because this is either the first render
      // or the size of the chart has changed
      width = chartWidth;
      height = chartHeight;
      gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
      gradient.addColorStop(0, '#2AB24A');
      gradient.addColorStop(1, '#07BEDF');
    }

    return gradient;
  };

  const chartData = {
    datasets: [
      {
        data: chartPercentage(),
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const chartArea = context.chart.chartArea;
          return [backgroundColor, iqEnabled ? getGradient(ctx, chartArea) : subjectColor];
        },
        hoverBackgroundColor: [backgroundColor, subjectColor],
        borderWidth: 0,
        borderRadius: 6,
        spacing: -2,
      },
    ],
  };

  const chartOptions = {
    animation: { animateRotate: animation },
    cutout,
    rotation: progress * 3.6,
    tooltips: { enabled: false },
    hover: { mode: null },
  };

  const plugins = [
    {
      id: 'centerText',
      afterDraw(chart) {
        const {
          ctx,
          chartArea: { left, top, right, bottom },
        } = chart;
        ctx.save();

        // Calculate center coordinates
        const centerX = (left + right) / 2;
        const centerY = (top + bottom) / 2;

        // Set font and style for the text
        ctx.font = '16px roboto';
        ctx.fillStyle = Theme.textColorPrimary;
        ctx.fontWeight = '500';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        ctx.fillText(`${progress}%`, centerX, centerY);
        ctx.restore();
      },
    },
  ];

  if (toolTipText)
    return (
      <MiniTooltip
        text={
          <div className={classes.tooltipText}>
            <span>{toolTipText}</span>
          </div>
        }
        position="custom"
        positionOffset={{ transform: 'translate(-45%, 10%)' }}
      >
        <div className={classes.chartContainer}>
          <span className={classes.doughnut}>
            <Doughnut data={chartData} options={chartOptions} plugins={plugins} />
          </span>
          <span className={classes.chartText}>{chartText}</span>
        </div>
      </MiniTooltip>
    );

  return (
    <div className={classes.chartContainer}>
      <span className={classes.doughnut}>
        <Doughnut data={chartData} options={chartOptions} plugins={plugins} />
      </span>
      <span className={classes.chartText}>{chartText}</span>
    </div>
  );
};

RateChart.propTypes = {
  progress: PropTypes.number,
  cutout: PropTypes.string,
  subjectColor: PropTypes.string,
  backgroundColor: PropTypes.string,
  animation: PropTypes.bool,
  chartText: PropTypes.object,
  toolTipText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  iqEnabled: PropTypes.bool,
};

export default RateChart;
